import React, { useState, useMemo } from 'react';
import { FaSearch, FaPen, FaCheck, FaTimes } from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import { showToast } from '../utils/toast';
import api from '../../../services/api';

const CompletedPage = () => {
  const { completed, brand, fetchClients } = useProContext();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [caValue, setCaValue] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (c) => {
    setEditingId(c.id);
    setCaValue(c.money_generated != null ? String(c.money_generated) : '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCaValue('');
  };

  const saveEdit = async (id) => {
    const value = Number(caValue);
    if (Number.isNaN(value) || value < 0) {
      showToast.error('Montant invalide');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/clients/change-ca/${id}`, { money_generated: value });
      showToast.success('CA mis à jour');
      if (fetchClients) await fetchClients();
      setEditingId(null);
      setCaValue('');
    } catch {
      showToast.error('Erreur lors de la mise à jour du CA');
    }
    setSaving(false);
  };

  const statusMap = {
    approved: { label: 'Approuvé', cls: 'bg-green-50 text-green-600' },
    delivered: { label: 'Livré', cls: 'bg-emerald-50 text-emerald-600' },
    rejected: { label: 'Rejeté', cls: 'bg-red-50 text-red-600' },
  };

  const sorted = useMemo(() => {
    return [...completed].sort((a, b) => {
      const aDate = new Date(a.updated_at || a.created_at).getTime();
      const bDate = new Date(b.updated_at || b.created_at).getTime();
      return bDate - aDate;
    });
  }, [completed]);

  const filtered = useMemo(() => {
    if (!search.trim()) return sorted;
    const q = search.toLowerCase();
    return sorted.filter(c => {
      const name = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      const sponsor = (c.sponsor_name || '').toLowerCase();
      return name.includes(q) || sponsor.includes(q);
    });
  }, [sorted, search]);

  const approved = completed.filter(c => c.status === 'approved' || c.status === 'delivered');
  const rejected = completed.filter(c => c.status === 'rejected');
  const totalCA = approved.reduce((sum, c) => sum + (Number(c.money_generated) || 0), 0);

  return (
    <ProLayout title="Recommandations traitées">
      <div className="p-5 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Total traitées</p>
            <p className="text-xl font-bold text-gray-900">{completed.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Approuvées</p>
            <p className="text-xl font-bold text-green-600">{approved.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">CA généré</p>
            <p className="text-xl font-bold" style={{ color: brand }}>{Math.ceil(totalCA)} €</p>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un client ou un recommandeur..."
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">Toutes les recommandations traitées</h3>
            <span className="text-[0.6875rem] text-gray-400">{filtered.length} résultat{filtered.length > 1 ? 's' : ''}{filtered.length !== completed.length ? ` sur ${completed.length}` : ''}</span>
          </div>
          <div className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-300 text-center py-12">{search.trim() ? 'Aucun résultat' : 'Aucune recommandation traitée'}</p>
            ) : filtered.map(c => {
              const st = statusMap[c.status] || { label: c.status, cls: 'bg-gray-50 text-gray-600' };
              const canEditCA = c.status === 'approved' || c.status === 'delivered';
              const isEditing = editingId === c.id;
              return (
                <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                    <p className="text-[0.6875rem] text-gray-400 mt-0.5">
                      par {c.sponsor_name} — {new Date(c.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canEditCA && (
                      isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={caValue}
                              onChange={e => setCaValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveEdit(c.id);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              disabled={saving}
                              className="w-24 pl-2 pr-6 py-1 text-sm font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 disabled:opacity-50"
                              style={{ '--tw-ring-color': brand }}
                              placeholder="Montant"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[0.6875rem] text-gray-400">€</span>
                          </div>
                          <button
                            onClick={() => saveEdit(c.id)}
                            disabled={saving}
                            className="p-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-md transition-colors disabled:opacity-50"
                            title="Valider"
                          >
                            <FaCheck className="w-3 h-3" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={saving}
                            className="p-1.5 bg-gray-100 text-gray-500 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                            title="Annuler"
                          >
                            <FaTimes className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(c)}
                          className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[0.75rem] font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors"
                          title="Modifier le CA"
                        >
                          <span>{c.money_generated ? `${Math.ceil(c.money_generated)} € CA` : 'Saisir CA'}</span>
                          <FaPen className="w-2.5 h-2.5 text-gray-400" />
                        </button>
                      )
                    )}
                    <span className={`text-[0.625rem] font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Toaster />
    </ProLayout>
  );
};

export default CompletedPage;
